// The split-editor allocation bar (§s05): each split part gets a segment
// (accent / accent-muted rotate), and any unallocated residual renders as a
// hatched slug -- never as a silent `0` or a segment that looks allocated.
import { AmountText } from '../AmountText';

const PART_COLORS = ['bg-link', 'bg-link-muted', 'bg-business', 'bg-confidence-med'];

export function SplitBar({
  parts,
  total,
  unallocated,
  className,
}: {
  parts: { amount: string; label: string }[];
  total: string;
  unallocated: string;
  className?: string;
}) {
  const totalNum = Math.max(1, Math.abs(Number(total)));
  const unallocatedNum = Math.abs(Number(unallocated));
  const hasResidual = unallocatedNum > 0.0001;

  return (
    <div className={className} data-testid="split-bar">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label="Split allocation">
        {parts.map((part, i) => (
          <span
            key={part.label}
            className={`h-full ${PART_COLORS[i % PART_COLORS.length]}`}
            style={{ width: `${Math.min(100, (Math.abs(Number(part.amount)) / totalNum) * 100)}%` }}
            data-testid="split-bar-segment"
          />
        ))}
        {hasResidual && (
          <span
            className="ms-hatch h-full"
            style={{ width: `${Math.min(100, (unallocatedNum / totalNum) * 100)}%` }}
            data-testid="split-bar-unallocated"
          />
        )}
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {parts.map((part, i) => (
          <li key={part.label} className="flex items-center gap-1.5">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${PART_COLORS[i % PART_COLORS.length]}`} />
            <span className="flex-1">{part.label}</span>
            <AmountText value={part.amount} />
          </li>
        ))}
        {hasResidual && (
          <li className="flex items-center gap-1.5 text-degraded-foreground">
            <span aria-hidden="true" className="ms-hatch h-2 w-2 rounded-sm" />
            <span className="flex-1">Unallocated</span>
            <AmountText value={unallocated} />
          </li>
        )}
      </ul>
    </div>
  );
}

export default SplitBar;
